import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  ArrowBackIosNew as ArrowBackIosNewIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  FileOpen as FileOpenIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { batchUpdateVersionNumbers, getVersionSnapshots, type IVersionSnapshot } from "../../../services/businessCaseService";

interface IVersionData {
  id: string;
  version: string;
  bc_file_name: string;
  last_updated_at: string;
  last_updated_by_name?: string;
}

interface BusinessCaseVersionsProps {
  versions: IVersionData[];
  businessCaseId: string;
  onVersionUpdate?: () => void;
}

interface IEditedVersion {
  id: string;
  newVersion: string;
  originalVersion: string;
}

const BusinessCaseVersions: React.FC<BusinessCaseVersionsProps> = ({
  versions,
  businessCaseId,
  onVersionUpdate,
}) => {
  const { t } = useTranslation();
  const [editedVersions, setEditedVersions] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [updating, setUpdating] = useState<boolean>(false);

  // Preview states
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewSnapshots, setPreviewSnapshots] = useState<IVersionSnapshot[]>([]);
  const [previewVersionFileName, setPreviewVersionFileName] = useState<string>("");
  const [previewSharePointUrl, setPreviewSharePointUrl] = useState<string | null>(null);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Initialize edited versions from props
  useEffect(() => {
    const initialVersions: Record<string, string> = {};
    versions.forEach((v) => {
      initialVersions[v.id] = v.version;
    });
    setEditedVersions(initialVersions);
    setHasChanges(false);
  }, [versions]);

  const handleVersionChange = (versionId: string, newValue: string) => {
    setEditedVersions((prev) => ({
      ...prev,
      [versionId]: newValue,
    }));
    setHasChanges(true);
  };

  const validateAllVersions = (): string | null => {
    // Get all version numbers as integers
    const versionNumbers: { id: string; version: number; original: string }[] = [];
    
    for (const [id, versionStr] of Object.entries(editedVersions)) {
      const versionInt = parseInt(versionStr, 10);
      
      // Check if it's a valid positive integer
      if (isNaN(versionInt) || versionInt <= 0 || versionStr.trim() !== versionInt.toString()) {
        const originalVersion = versions.find((v) => v.id === id);
        return `Version for "${originalVersion?.bc_file_name}" must be a positive integer.`;
      }
      
      versionNumbers.push({
        id,
        version: versionInt,
        original: versions.find((v) => v.id === id)?.version || "",
      });
    }

    // Check for duplicates
    const versionValues = versionNumbers.map((v) => v.version);
    const duplicates = versionValues.filter((v, i) => versionValues.indexOf(v) !== i);
    if (duplicates.length > 0) {
      return `Duplicate version numbers found: ${[...new Set(duplicates)].join(", ")}. Each version must be unique.`;
    }

    // Sort by version number
    const sortedVersions = [...versionNumbers].sort((a, b) => a.version - b.version);

    // Check that sequence starts from 1
    if (sortedVersions[0].version !== 1) {
      const missingRange = Array.from({ length: sortedVersions[0].version - 1 }, (_, i) => i + 1);
      return `Version sequence must start from 1. Currently missing: ${missingRange.join(", ")}.`;
    }

    // Check for gaps in the sequence
    for (let i = 0; i < sortedVersions.length - 1; i++) {
      const current = sortedVersions[i].version;
      const next = sortedVersions[i + 1].version;

      if (next - current > 1) {
        const missingVersions = Array.from({ length: next - current - 1 }, (_, idx) => current + idx + 1);
        return `Missing versions in sequence: ${missingVersions.join(", ")}. Version numbers must be consecutive.`;
      }
    }

    return null;
  };

  const handleSaveChanges = async () => {
    // Validate all versions
    const error = validateAllVersions();
    if (error) {
      toast.error(error);
      return;
    }

    // Find which versions have changed
    const changedVersions: IEditedVersion[] = [];
    versions.forEach((v) => {
      const newVersion = editedVersions[v.id];
      if (newVersion !== v.version) {
        changedVersions.push({
          id: v.id,
          newVersion: newVersion,
          originalVersion: v.version,
        });
      }
    });

    if (changedVersions.length === 0) {
      toast.info(t("version_preview.no_changes_to_save"));
      setHasChanges(false);
      return;
    }

    setUpdating(true);

    try {
      // Prepare batch update payload - send ALL versions with their new numbers
      const allVersionUpdates = Object.entries(editedVersions).map(([id, version]) => ({
        id,
        version,
      }));

      // Call batch update endpoint
      await batchUpdateVersionNumbers(businessCaseId, allVersionUpdates);

      toast.success(t("version_preview.successfully_updated_versions", { count: changedVersions.length }));
      setHasChanges(false);

      // Trigger refresh if callback provided
      if (onVersionUpdate) {
        onVersionUpdate();
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || t("version_preview.failed_to_update_versions");
      toast.error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelChanges = () => {
    // Reset to original versions
    const originalVersions: Record<string, string> = {};
    versions.forEach((v) => {
      originalVersions[v.id] = v.version;
    });
    setEditedVersions(originalVersions);
    setHasChanges(false);
  };

  // Preview handlers
  const handlePreviewClick = async (versionId: string, fileName: string) => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    setPreviewVersionFileName(fileName);
    setPreviewSnapshots([]);
    setPreviewSharePointUrl(null);
    setCurrentPreviewIndex(0);

    try {
      const response = await getVersionSnapshots(businessCaseId, versionId);
      setPreviewSnapshots(response.snapshots || []);
      setPreviewSharePointUrl(response.sharepoint_fallback || null);
      // No error toasts - graceful fallback
    } catch (error: any) {
      console.error("Error fetching version snapshots:", error);
      // Silently fail - no error toast shown to user
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewSnapshots([]);
    setPreviewVersionFileName("");
    setPreviewSharePointUrl(null);
    setCurrentPreviewIndex(0);
  };

  const handleLightboxOpen = (index: number) => {
    setCurrentPreviewIndex(index);
    setLightboxOpen(true);
  };

  const handleLightboxClose = () => {
    setLightboxOpen(false);
  };

  const handlePrevImage = () => {
    setCurrentPreviewIndex((prev) =>
      prev === 0 ? previewSnapshots.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    setCurrentPreviewIndex((prev) =>
      prev === previewSnapshots.length - 1 ? 0 : prev + 1
    );
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Sort versions by version number (descending - newest first)
  const sortedVersions = [...versions].sort((a, b) => {
    const versionA = parseInt(a.version, 10);
    const versionB = parseInt(b.version, 10);
    return versionB - versionA;
  });

  return (
    <Box>
      {/* Action Buttons */}
      {hasChanges && (
        <Box sx={{ mb: 2, display: "flex", gap: 2, justifyContent: "flex-end" }}>
          <Button
            variant="outlined"
            onClick={handleCancelChanges}
            disabled={updating}
            sx={{
              textTransform: "none",
              borderColor: "#757575",
              color: "#757575",
              "&:hover": {
                borderColor: "#424242",
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            {t("version_preview.cancel_changes")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveChanges}
            disabled={updating}
            sx={{
              textTransform: "none",
              backgroundColor: "#c4262e",
              color: "white",
              "&:hover": {
                backgroundColor: "#a01e24",
              },
              "&:disabled": {
                backgroundColor: "#f5c6c3",
                color: "#fff",
              },
            }}
          >
            {updating ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                {t("version_preview.saving")}
              </>
            ) : (
              t("version_preview.save_changes")
            )}
          </Button>
        </Box>
      )}

      <TableContainer component={Paper} elevation={0}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: 600, width: "150px", height: "36px", py: 2 }}>{t("version_preview.version_number")}</TableCell>
              <TableCell sx={{ fontWeight: 600, height: "36px", py: 2 }}>{t("version_preview.business_case_file_name")}</TableCell>
              <TableCell sx={{ fontWeight: 600, height: "36px", py: 2 }}>{t("version_preview.last_updated")}</TableCell>
              <TableCell sx={{ fontWeight: 600, height: "36px", py: 2 }}>{t("version_preview.updated_by")}</TableCell>
              <TableCell sx={{ fontWeight: 600, width: "100px", height: "36px", py: 2, textAlign: "center" }}>{t("version_preview.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedVersions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t("version_preview.no_versions_found")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedVersions.map((version) => (
                <TableRow
                  key={version.id}
                  sx={{
                    "&:hover": { backgroundColor: "#fafafa" },
                    transition: "background-color 0.2s",
                  }}
                >
                  <TableCell>
                    <TextField
                      value={editedVersions[version.id] || version.version}
                      onChange={(e) => handleVersionChange(version.id, e.target.value)}
                      size="small"
                      type="number"
                      disabled={updating}
                      sx={{
                        width: "100px",
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: hasChanges && editedVersions[version.id] !== version.version
                            ? "#fff9c4"
                            : "white",
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        },
                      }}
                      inputProps={{
                        min: 1,
                        style: { textAlign: "center", fontWeight: 600 },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {version.bc_file_name || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(version.last_updated_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {version.last_updated_by_name || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <Tooltip title={t("version_preview.preview_snapshots")}>
                      <IconButton
                        size="small"
                        onClick={() => handlePreviewClick(version.id, version.bc_file_name || "Unknown File")}
                        sx={{
                          color: "#c4262e",
                          "&:hover": {
                            backgroundColor: "rgba(196, 38, 46, 0.08)",
                          },
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Preview Modal */}
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: "90vh",
          },
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Modal Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              borderBottom: "1px solid #e0e0e0",
              pb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: "#333" }}>
                {t("version_preview.preview_snapshots")}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {previewVersionFileName}
              </Typography>
            </Box>
            <IconButton onClick={handlePreviewClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Modal Content */}
          {previewLoading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
              }}
            >
              <CircularProgress size={48} sx={{ color: "#c4262e" }} />
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mt: 2, fontWeight: 500 }}
              >
                {t("version_preview.loading_preview_snapshots")}
              </Typography>
            </Box>
          ) : previewSnapshots.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                py: 8,
                gap: 2,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                {t("version_preview.no_snapshots_available")}
              </Typography>
              {previewSharePointUrl && (
                <Button
                  variant="contained"
                  startIcon={<FileOpenIcon />}
                  onClick={() => window.open(previewSharePointUrl, "_blank")}
                  sx={{
                    textTransform: "none",
                    backgroundColor: "#c4262e",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#a01e24",
                    },
                  }}
                >
                  {t("version_preview.open_excel_on_sharepoint") || "Open Excel File"}
                </Button>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              {previewSnapshots.map((snapshot, index) => (
                <Box
                  key={index}
                  onClick={() => handleLightboxOpen(index)}
                  sx={{
                    cursor: "pointer",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #e0e0e0",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      transform: "scale(1.02)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <img
                      src={snapshot.blob_url}
                      alt={snapshot.sheet_name || "Snapshot"}
                      style={{
                        width: "280px",
                        height: "180px",
                        objectFit: "cover",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        backgroundColor: "rgba(0,0,0,0.6)",
                        color: "white",
                        px: 1.5,
                        py: 0.75,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 500,
                          display: "block",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {snapshot.sheet_name || t("version_preview.sheet_preview")}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Dialog>

      {/* Lightbox Dialog for Full View */}
      <Dialog
        open={lightboxOpen}
        onClose={handleLightboxClose}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            maxWidth: "95vw",
            maxHeight: "95vh",
            m: 2,
            borderRadius: 2,
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleLightboxClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#c4262e",
              backgroundColor: "rgba(255,255,255,0.9)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Previous Button */}
          {previewSnapshots.length > 1 && (
            <IconButton
              onClick={handlePrevImage}
              sx={{
                position: "absolute",
                left: 8,
                color: "#c4262e",
                backgroundColor: "rgba(255,255,255,0.9)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,1)",
                },
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          )}

          {/* Image */}
          {previewSnapshots[currentPreviewIndex] && (
            <Box sx={{ textAlign: "center" }}>
              <img
                src={previewSnapshots[currentPreviewIndex].blob_url}
                alt={previewSnapshots[currentPreviewIndex].sheet_name || "Snapshot"}
                style={{
                  maxWidth: "90vw",
                  maxHeight: "80vh",
                  objectFit: "contain",
                }}
              />
              <Typography
                variant="body2"
                sx={{ color: "white", mt: 2, fontWeight: 500 }}
              >
                {previewSnapshots[currentPreviewIndex].sheet_name || "Sheet Preview"}
                {previewSnapshots.length > 1 && ` (${currentPreviewIndex + 1} of ${previewSnapshots.length})`}
              </Typography>
            </Box>
          )}

          {/* Next Button */}
          {previewSnapshots.length > 1 && (
            <IconButton
              onClick={handleNextImage}
              sx={{
                position: "absolute",
                right: 8,
                color: "#c4262e",
                backgroundColor: "rgba(255,255,255,0.9)",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,1)",
                },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default BusinessCaseVersions;
